import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useSession } from '../../state/session.ts';
import { onboarding } from '../onboarding/actions.ts';
import { Button } from '../../components/Button.tsx';
import { ApprovalReview } from './ApprovalReview.tsx';
import { useConfirmation } from './controller.ts';
import { LetterDetails } from './LetterDetails.tsx';
import { PublicLayout } from './PublicLayout.tsx';
import { DomainPublication } from './DomainPublication.tsx';
import styles from './authorization.module.css';
export function ConfirmationPage() {
  const state = useConfirmation();
  const navigate = useNavigate();
  // An approval finishes setup, so it waits for the records; a management link is a returning owner.
  const [published, setPublished] = useState(false);
  const inSetup = state.letter?.purpose !== 'manage';
  return (
    <PublicLayout title="Review authorization">
      {state.error && (
        <p role="alert" className={styles.error}>
          {state.error}
        </p>
      )}
      {state.confirmed ? (
        <section className={`${styles.panel} ${styles.confirmationPanel}`}>
          <h2>
            {state.letter?.purpose === 'manage'
              ? 'Owner access confirmed'
              : 'Authorization recorded and signed'}
          </h2>
          <p role="status">
            {inSetup ? '' : `The recorded state is ${state.confirmed.status}. `}Without an account,
            this device stays signed in for up to 8 hours after approval; then sign in with{' '}
            {state.confirmed.terms.authorizer.email}.
          </p>
          <LetterDetails terms={state.confirmed.terms} />
          <p>
            {inSetup && !published
              ? 'The private letter is signed. Publish the records below in your DNS to finish setup, then continue to your workspace on this device.'
              : 'The private letter is signed and its separate public summary is published. Continue to your workspace on this device.'}{' '}
            Another tab or device where setup started asks you to sign in.
          </p>
          <DomainPublication letter={state.confirmed} collapsed={!inSetup} onDone={setPublished} />
          {state.workspace ? (
            <Button
              variant="primary"
              isDisabled={inSetup && !published}
              onPress={() => {
                if (!state.workspace) return;
                useSession.getState().start(state.workspace);
                onboarding.reset();
                void navigate({ to: '/', replace: true });
              }}
            >
              Enter workspace
            </Button>
          ) : (
            <Button
              onPress={() => {
                if (state.confirmed) void state.prepareWorkspace(state.confirmed.id);
              }}
            >
              Retry workspace access
            </Button>
          )}
          <Link to="/owner/$id" params={{ id: state.confirmed.id }}>
            Download the private letter or revoke
          </Link>
        </section>
      ) : state.letter ? (
        <section className={`${styles.panel} ${styles.confirmationPanel}`}>
          <ApprovalReview
            letter={state.letter}
            purpose={state.letter.purpose}
            expiresAt={state.letter.linkExpiresAt}
            busy={state.busy}
            onConfirm={() => void state.confirm()}
          />
        </section>
      ) : !state.error ? (
        <p role="status">Loading your private letter…</p>
      ) : (
        <p>
          <a href="/">Return to Traceira to request a new link</a>
        </p>
      )}
    </PublicLayout>
  );
}
